import { useTranslation } from 'react-i18next';
import LoaderIcon from '@/public/icons/approval/loader.svg';
import CheckIcon from '@/public/icons/approval/check.svg';
import AlertIcon from '@/public/icons/alert.svg';
import StatusBadgeBase from '@/components/Approval/ApprovalView/StatusBadgeBase';
import { APPROVAL_STATUS_BADGE } from '@/components/Approval/_tokens/statusColors';
import type { ApprovalStatusTone } from '@/components/Approval/_tokens/statusColors';
import {
  CONTRACT_STATUS_BY_CODE,
  CONTRACT_STATUS_LABEL,
  type ContractStatus,
} from './types';

/**
 * e-law 계약 상태 딱지. 품의 상태 딱지와 같은 부품·같은 팔레트를 쓴다 —
 * 한 카드 목록에 품의와 계약이 섞이므로 모양이 다르면 같은 뜻인지 알 수 없다.
 *
 * 완료(success)와 반려(danger)만 결과 상태이고 나머지 여섯은 진행 단계라 info 로 묶는다.
 * 매핑에 없는 코드는 서버가 함께 주는 상태명을 그대로 찍는다(설계서 2-4).
 *
 * 서버는 e-law 숫자 코드(120 등)와 상태명을 함께 준다. 코드로 톤을 정하고 이름은 서버 것을
 * 우선 쓴다 — "원본회수대기중"처럼 서버 이름이 8종 라벨보다 구체적이다.
 * 표에 없는 코드는 회색에 아이콘 없이 이름만 찍는다. 스피너를 달면 진행 중처럼 읽힌다.
 */
const TONE: Record<ContractStatus, ApprovalStatusTone> = {
  drafting: 'info',
  reviewing: 'info',
  negotiating: 'info',
  approvalPending: 'info',
  signing: 'info',
  collectPending: 'info',
  completed: 'success',
  rejected: 'danger',
};

type ContractStatusBadgeProps = {
  status: string;
  /** 매핑에 없는 코드일 때 서버가 준 상태명 */
  fallbackLabel?: string;
};

const ContractStatusBadge = ({
  status,
  fallbackLabel,
}: ContractStatusBadgeProps) => {
  const { t } = useTranslation();
  // 목업은 8종 키를, 서버는 숫자 코드를 준다. 둘 다 받는다.
  const mapped: ContractStatus | undefined =
    status in CONTRACT_STATUS_LABEL
      ? (status as ContractStatus)
      : CONTRACT_STATUS_BY_CODE[status];
  const label =
    fallbackLabel ?? (mapped ? CONTRACT_STATUS_LABEL[mapped] : undefined);
  if (!label) return null;

  const tone: ApprovalStatusTone = mapped ? TONE[mapped] : 'neutral';
  const Icon =
    tone === 'success'
      ? CheckIcon
      : tone === 'danger'
      ? AlertIcon
      : tone === 'info'
      ? LoaderIcon
      : null;

  return (
    <StatusBadgeBase
      variant="tag"
      tone={tone}
      label={t(label)}
      palette={APPROVAL_STATUS_BADGE[tone]}
      customPrefix={
        Icon ? <Icon width={16} height={16} aria-hidden /> : undefined
      }
    />
  );
};

export default ContractStatusBadge;
